import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';

import TextInput from '../Parts/TextInput';
import StateSelector from '../Parts/StateSelector';
import Styles from '../Styles';
import BoolInput from '../Parts/BoolInput';
import Button from '../Parts/Button';
import type { EditableAddress, ExistingAddress } from './addressTypes';

const normalizeInitialAddress = (initialAddress?: ExistingAddress | null): EditableAddress => ({
	id: initialAddress?.id,
	address_id: initialAddress?.address_id,
	snapshot_id: initialAddress?.snapshot_id,
	creation_date: initialAddress?.creation_date,
	company_name: initialAddress?.company_name ?? '',
	first_name: initialAddress?.first_name ?? '',
	last_name: initialAddress?.last_name ?? '',
	address: initialAddress?.address ?? initialAddress?.address1 ?? '',
	address1: initialAddress?.address1 ?? null,
	address2: initialAddress?.address2 ?? '',
	state: initialAddress?.state ?? '',
	state_code: initialAddress?.state_code ?? '',
	country: initialAddress?.country ?? '',
	country_subdivision_id: initialAddress?.country_subdivision_id,
	city: initialAddress?.city ?? '',
	zip: initialAddress?.zip ?? '',
	phone: initialAddress?.phone ?? '',
	email: initialAddress?.email ?? '',
	is_residential: initialAddress?.is_residential === undefined || initialAddress?.is_residential === null
		? null
		: (initialAddress.is_residential === true || (initialAddress.is_residential as unknown) === 1),
	lift_gate: initialAddress?.lift_gate === undefined || initialAddress?.lift_gate === null
		? null
		: initialAddress.lift_gate == 1,
});

const toApiAddress = (address: EditableAddress): ExistingAddress => {
	const {
		id,
		address_id: addressId,
		snapshot_id: snapshotId,
		creation_date: creationDate,
		country_subdivision_id: countrySubdivisionId,
		is_residential: isResidential,
		lift_gate: liftGate,
		...restAddress
	} = address;

	const apiAddress: ExistingAddress = {
		...restAddress,
		...(isResidential !== null ? { is_residential: isResidential } : {}),
		...(liftGate !== null ? { lift_gate: (liftGate ? 1 : 0) } : {}),
	};

	if (id !== undefined) {
		apiAddress.id = id;
	}
	if (addressId !== undefined) {
		apiAddress.address_id = addressId;
	}
	if (snapshotId !== undefined) {
		apiAddress.snapshot_id = snapshotId;
	}
	if (creationDate !== undefined) {
		apiAddress.creation_date = creationDate;
	}
	if (countrySubdivisionId !== undefined) {
		apiAddress.country_subdivision_id = countrySubdivisionId;
	}

	return apiAddress;
};

const Hr = () => (
	<hr
		style={{
			background: 'gray',
			border: 'none',
			height: '1px',
			margin: '1.4em 1em 1em 1em',
		}}
	/>
);

type AddressEditorProps = {
	onCancel: () => void;
	onSet: (address: ExistingAddress) => void;
	label?: string;
	initialAddress?: ExistingAddress | null;
	quoteType?: string;
};

const AddressEditor = ({ onCancel, onSet, label, initialAddress, quoteType }: AddressEditorProps) => {
	const [address, setAddress] = useState<EditableAddress>(normalizeInitialAddress(initialAddress));

	const [optionStyle, setOptionStyle] = useState<React.CSSProperties>({
		border: "1px solid #FFEED7",
		borderRadius: "4px",
		transition: "all .2s"
	});
	const [emphasizing, setEmphasizing] = useState(false);

	const getTextStyle = (value: unknown): React.CSSProperties => (((value === null || value === undefined || value === "") && emphasizing)
		? { ...optionStyle, width: "100%" }
		: { width: "100%", transition: "all .2s" });

	const getBoolStyle = (value: unknown): React.CSSProperties => (((value === null || value === undefined) && emphasizing)
		? optionStyle
		: { border: "1px solid transparent", borderRadius: "4px", transition: "all .2s" });

	const emphasizeOptions = () => {
		const initialOption = { border: "1px solid #FFEED7", borderRadius: "4px", transition: "all .2s" };
		const optionEmphasis = {
			border: "solid 1px #ffae47",
			borderRadius: "4px",
			backgroundColor: "#FFEED7",
			transition: "all .2s"
		};

		setEmphasizing(true);
		setOptionStyle(optionEmphasis);

		setTimeout(() => {
			setOptionStyle(initialOption);
		}, 400);
		setTimeout(() => {
			setOptionStyle(optionEmphasis);
		}, 800);
		setTimeout(() => {
			setOptionStyle(initialOption);
		}, 1200);
		setTimeout(() => {
			setOptionStyle(optionEmphasis);
		}, 1600);
		setTimeout(() => {
			setOptionStyle(initialOption);
		}, 2000);
		setTimeout(() => {
			setOptionStyle(optionEmphasis);
		}, 2400);
		setTimeout(() => {
			setOptionStyle(initialOption);
			setEmphasizing(false);
		}, 2800);
	};

	const addressChanged = (options: Partial<EditableAddress>) => {
		const newAddress = { ...address, ...options };

		setAddress(newAddress);
	};

	const submitAddress = () => {
		const REQUIRED: Array<{ label: string; varName: keyof EditableAddress }> = [
			{ label: 'Company Name', varName: 'company_name' },
			{ label: 'First Name', varName: 'first_name' },
			{ label: 'Last Name', varName: 'last_name' },
			{ label: 'Address', varName: 'address' },
			{ label: 'State', varName: 'country_subdivision_id' },
			{ label: 'City', varName: 'city' },
			{ label: 'Zip / Postal Code', varName: 'zip' },
			{ label: 'Is Residential Address', varName: 'is_residential' },
			{ label: 'Address Has Lift Gate', varName: 'lift_gate' },
		];

		const missingFields = REQUIRED.filter((req) =>
			address[req.varName] === null
				|| address[req.varName] === ''
				|| address[req.varName] === undefined);

		if (missingFields.length > 0) {
			emphasizeOptions();
		} else {
			onSet && onSet(toApiAddress(address));
		}
	};

	return (
		<div id="address-modal-wrapper">
			<div
				className="quote-address address-modal address-modal-two"
				style={{
					width: '30em',
					maxWidth: '95vw',
					maxHeight: '100vh',
				}}
			>
				<Container className="local-bootstrap">
					<Row>
						<Col>
							<h4 className={`text-${quoteType}`}>{label || 'New Address'}</h4>
						</Col>
					</Row>
					<Row>
						<Col>
							<TextInput
								style={getTextStyle(address.company_name)}
								label="Company Name"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ width: '100%', marginBottom: '0.5em' }}
								onChange={(companyName) => addressChanged({ company_name: companyName })}
														defaultValue={address.company_name}
								required
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<TextInput
								label="First Name"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ marginRight: '3%', flexGrow: '1' }}
								style={getTextStyle(address.first_name)}
								onChange={(firstName) => addressChanged({ first_name: firstName })}
														defaultValue={address.first_name}
								required
							/>
						</Col>
						<Col>
							<TextInput
								label="Last Name"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: '1' }}
								style={getTextStyle(address.last_name)}
								onChange={(lastName) => addressChanged({ last_name: lastName })}
														defaultValue={address.last_name}
								required
							/>
						</Col>
					</Row>

					<Hr />
					<Row>
						<Col>
							<span>Is Residential Address</span>
							<br />
							<BoolInput
								style={getBoolStyle(address.is_residential)}
								onChange={(newVal) => addressChanged({ is_residential: newVal })}
								value={address.is_residential}
								nullable
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<span>Lift Gate Required</span>
							<br />
							<BoolInput
								style={getBoolStyle(address.lift_gate)}
								onChange={(newVal) => addressChanged({ lift_gate: newVal })}
								value={address.lift_gate}
								nullable
							/>
						</Col>
					</Row>

					<Hr />
					<Row>
						<Col>
							<TextInput
								label="Address Line 1"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ width: '100%', marginBottom: '0.5em' }}
														style={getTextStyle(address.address)}
								onChange={(newAddress) => addressChanged({ address: newAddress })}
														defaultValue={address.address}
								required
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<TextInput
								label="Address Line 2"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ width: '100%', marginBottom: '0.5em' }}
								style={{ width: '100%' }}
								onChange={(address2) => addressChanged({ address2 })}
														defaultValue={address.address2}
							/>
						</Col>
					</Row>
					<Row>
						<Col>
							<StateSelector
								key={address.country_subdivision_id}
								label="State / Province"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: '1', marginBottom: '0.5em' }}
								style={getTextStyle(address.country_subdivision_id)}
								onChange={(countrySubdivisionId) =>
									addressChanged({ country_subdivision_id: countrySubdivisionId })}
								defaultValue={address.country_subdivision_id}
								required
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<TextInput
								label="City"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: '1', marginRight: '3%' }}
								style={getTextStyle(address.city)}
								onChange={(city) => addressChanged({ city })}
														defaultValue={address.city}
								required
							/>
						</Col>
						<Col>
							<TextInput
								label="Zip / Postal Code"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: '1' }}
								style={getTextStyle(address.zip)}
								onChange={(zip) => addressChanged({ zip })}
														defaultValue={address.zip}
								required
							/>
						</Col>
					</Row>

					<Hr />

					<Row>
						<Col>
							<TextInput
								label="Email"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: '1' }}
								style={{ width: '95%' }}
								onChange={(email) => addressChanged({ email })}
														defaultValue={address.email}
							/>
						</Col>
						<Col>
							<TextInput
								label="Phone"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: '1' }}
								style={{ width: '100%' }}
								onChange={(phone) => addressChanged({ phone })}
														defaultValue={address.phone}
							/>
						</Col>
					</Row>

					<Row className="mt-2">
						<Col>
							<Button
								color="gray"
								onClick={onCancel}
							>
								Cancel
							</Button>
						</Col>
						<Col className="text-end">
							<Button
								color={quoteType ?? 'green'}
								onClick={submitAddress}
							>
								Set Address
							</Button>
						</Col>
					</Row>
				</Container>
			</div>
		</div>
	);
};

export default AddressEditor;
